/**
 * NotificationPreferences - what this person wants to be told about
 * ------------------------------------------------------------------
 * BOTH ROLES. A poster gets messages and applications; a student gets
 * messages and job alerts. Only the job-alert filtering is student-shaped,
 * and that stays in NotificationSettings.jsx below this card.
 *
 * Two switches, and they are different in kind:
 *
 *   Everything   the master switch. Off means nothing buzzes at all, and
 *                routine news stops reaching the bell too.
 *   Messages     somebody writing to you about a job you are doing together.
 *
 * BOTH DEFAULT TO ON, unlike job alerts which are off until asked for. Job
 * alerts are the site starting to buzz about work nobody asked to hear about,
 * so they need a decision first. These two are about things aimed AT you
 * personally, and defaulting them off would mean people silently missing
 * messages meant for them.
 *
 * THE MASTER SWITCH IS NOT ABSOLUTE, AND THE SCREEN SAYS SO. A few things
 * still reach the bell with everything off - a job you are booked on being
 * cancelled, or a moderator taking your chore down - because not knowing
 * those costs somebody something real. They never buzz. Saying that here is
 * the point: a switch that quietly does less than it says is worse than one
 * that explains itself. See ALWAYS_TELL_THEM in backend/utils/notify.js.
 *
 * Saving is immediate - there is no Save button. Each switch is one fact, and
 * a form that needs confirming is a form people leave half-set.
 */

import { useState } from 'react';
import { BellRing, MessageSquare } from 'lucide-react';

import { authApi } from '../services/api';
import { useI18n } from '../hooks/useI18n';

export default function NotificationPreferences({ user, onSaved }) {
  const { t } = useI18n();

  // An account made before these existed has no preferences at all, and that
  // means the defaults rather than "off".
  const [prefs, setPrefs] = useState({
    enabled: user.notifications?.enabled !== false,
    messages: user.notifications?.messages !== false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const change = async (patch) => {
    const next = { ...prefs, ...patch };
    // Move the switch first. Waiting for the server makes a toggle feel
    // broken, and the next load corrects it if the save failed.
    setPrefs(next);
    setBusy(true);
    setError('');
    try {
      const { user: saved } = await authApi.updateProfile({ notifications: next });
      if (onSaved) onSaved(saved);
    } catch (err) {
      setError(err.message);
      setPrefs(prefs); // put it back where it was
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
      <h3 style={{ marginBottom: 'var(--space-xs)' }}>
        <BellRing size={20} aria-hidden="true" /> {t('prefs.title')}
      </h3>
      <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
        {t('prefs.subtitle')}
      </p>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>
          {error}
        </div>
      )}

      <div className="field">
        <label className="row" style={{ gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={prefs.enabled}
            disabled={busy}
            onChange={(e) => change({ enabled: e.target.checked })}
          />
          <span>{t('prefs.everything')}</span>
        </label>
        <span className="hint">{t('prefs.everythingHint')}</span>
      </div>

      {/* Only offered while the master switch is on. A sub-setting under a
          switch that is already off is a control that does nothing, and
          somebody will spend a minute wondering why it made no difference. */}
      {prefs.enabled && (
        <div className="field">
          <label className="row" style={{ gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={prefs.messages}
              disabled={busy}
              onChange={(e) => change({ messages: e.target.checked })}
            />
            <span className="row" style={{ gap: 6 }}>
              <MessageSquare size={16} aria-hidden="true" />
              {t('prefs.messages')}
            </span>
          </label>
          <span className="hint">{t('prefs.messagesHint')}</span>
        </div>
      )}

      {!prefs.enabled && (
        <div className="alert alert-warning">
          <div className="text-sm">{t('prefs.offWarning')}</div>
        </div>
      )}
    </div>
  );
}
